import React from 'react'
import { observer } from 'mobx-react-lite'

import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { Icon, MerchantLogo, Text } from '@mxenabled/mxui'

import { ListItemWrapper, useGlobalCopyStore, useTransactionStore } from '../../../../common'
import { Transaction, TransactionType } from '../../../../core'

import { postInsightOnTransaction } from '../../../../common/utils/PostMessage'

import { ON_CLICK_EVENTS } from '../../../constants/OnClickEvents'
import { useTransactionsUiStore } from '../../../context/hooks'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'

interface TransactionRowProps {
  component?: React.ElementType
  onClick?: (guid: string) => void
  transaction: Transaction
}

const TransactionRow: React.FC<TransactionRowProps> = ({ transaction, onClick, component }) => {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  const { associatedBeats } = useTransactionStore()
  const { expandedSplits } = useTransactionsUiStore()
  const { transactions: copy } = useGlobalCopyStore()
  const { showInsights } = useTransactionsUiStore()

  const isCredit = transaction.transaction_type === TransactionType.CREDIT

  const beat = React.useMemo(() => {
    return associatedBeats.find((beat) => beat.associated_transaction_guid === transaction.guid)
  }, [associatedBeats, transaction])

  const getTransactionStatusIcons = (
    transaction: Transaction,
    isDarkMode: boolean,
  ): React.ReactNode[] => {
    const icons: React.ReactNode[] = []

    if (transaction.is_flagged) {
      icons.push(
        <Icon
          fill={true}
          key="flag"
          name="flag"
          size={16}
          sx={{ color: isDarkMode ? 'primary.main' : 'warning.dark' }}
        />,
      )
    }

    if (transaction.is_hidden) {
      icons.push(<Icon key="hidden" name="visibility_off" size={16} />)
    }

    if (transaction.has_been_split) {
      icons.push(<Icon key="split" name="call_split" size={16} />)
    }

    return icons
  }

  const getTransactionCategoryText = (transaction: Transaction, copy: any): string => {
    if (transaction.is_hidden) {
      return copy.is_excluded
    }

    if (transaction.has_been_split) {
      return copy.split_transaction_category_label
    }

    return transaction.category || ''
  }

  return (
    //TODO: implement common/components/ListItemRow.tsx
    <ListItemWrapper
      clickEventName={ON_CLICK_EVENTS.ON_TRANSACTION_LIST_ITEM_CLICK}
      {...(component && { component })}
      onClick={onClick ? () => onClick(transaction.guid) : undefined}
      sx={
        transaction.is_hidden ? { backgroundColor: 'grey.50', color: 'text.secondary' } : undefined
      }
    >
      <ListItemAvatar>
        {transaction.parent_guid && expandedSplits.includes(transaction.parent_guid) ? (
          <React.Fragment />
        ) : (
          <MerchantLogo
            categoryGuid={transaction.top_level_category_guid || ''}
            merchantGuid={transaction.merchant_guid || ''}
          />
        )}
      </ListItemAvatar>
      <ListItemText
        disableTypography={true}
        secondary={
          <Stack direction="row" justifyContent="space-between">
            <Stack alignItems="center" direction="row" gap={4}>
              {getTransactionStatusIcons(transaction, isDarkMode)}
              <Text truncate={true} variant="caption">
                {getTransactionCategoryText(transaction, copy)}
              </Text>
            </Stack>
            {beat && showInsights && (
              <Text
                bold={true}
                color="action.active"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()

                  postInsightOnTransaction(beat.guid)
                }}
                truncate={true}
                variant="caption"
              >
                {beat.short_title}
              </Text>
            )}
          </Stack>
        }
      >
        <Stack direction="row" justifyContent="space-between" sx={{ gap: 8 }}>
          <Text bold={true} truncate={true} variant="body1">
            {transaction.payee || transaction.description || transaction.feed_description}
          </Text>
          <Text
            bold={true}
            color={isCredit ? 'success.main' : 'text.primary'}
            sx={{ flexShrink: 0 }}
            variant="body1"
          >
            {isCredit ? '+' : ''}
            {formatCurrency(transaction.amount, '0,0.00')}
          </Text>
        </Stack>
      </ListItemText>
    </ListItemWrapper>
  )
}

export default observer(TransactionRow)
